import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  Search,
  AlertTriangle,
  RefreshCw,
  MessageSquare,
  UserX,
  Calendar,
  Phone,
  TrendingDown,
  BarChart3,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Shield,
} from 'lucide-react';

// shadcn/ui components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ============================================================================
// Types
// ============================================================================

interface NoShowRecord {
  id: string;
  bookingId: string;
  businessId: string;
  customerId: string;
  scheduledAt: string;
  serviceName: string;
  servicePrice?: number;
  staffName?: string;
  detectedAt: string;
  recoveryStatus: string;
  recoveryAttempts: number;
  customerName: string;
  customerPhone: string;
  businessName: string;
  reliabilityScore: number;
  totalNoShows: number;
  totalBookings: number;
}

interface NoShowSummary {
  totalNoShows: number;
  pendingRecovery: number;
  successfulRecoveries: number;
  recoveryRate: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatCurrency(cents: number | undefined | null): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((cents || 0) / 100);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getReliabilityColor(score: number): string {
  if (score >= 90) return 'text-green-600 dark:text-green-500';
  if (score >= 75) return 'text-blue-600 dark:text-blue-500';
  if (score >= 50) return 'text-yellow-600 dark:text-yellow-500';
  return 'text-red-600 dark:text-red-500';
}

function getReliabilityBadge(score: number): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
} {
  if (score >= 90) return { label: 'excellent', variant: 'default' };
  if (score >= 75) return { label: 'good', variant: 'secondary' };
  if (score >= 50) return { label: 'fair', variant: 'outline' };
  return { label: 'poor', variant: 'destructive' };
}

function getRecoveryStatusBadge(status: string): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: React.ReactNode;
} {
  switch (status) {
    case 'PENDING':
      return { label: 'pending', variant: 'outline', icon: <Clock className="h-3 w-3" /> };
    case 'MESSAGE_SENT':
      return { label: 'messageSent', variant: 'secondary', icon: <Send className="h-3 w-3" /> };
    case 'RESPONDED':
      return { label: 'responded', variant: 'default', icon: <MessageSquare className="h-3 w-3" /> };
    case 'RESCHEDULED':
      return { label: 'rescheduled', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> };
    case 'CLOSED':
      return { label: 'closed', variant: 'secondary', icon: <XCircle className="h-3 w-3" /> };
    case 'EXPIRED':
      return { label: 'expired', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> };
    default:
      return { label: status, variant: 'outline', icon: null };
  }
}

// ============================================================================
// Main Component
// ============================================================================

function NoShowsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // State
  const [noShows, setNoShows] = useState<NoShowRecord[]>([]);
  const [summary, setSummary] = useState<NoShowSummary>({
    totalNoShows: 0,
    pendingRecovery: 0,
    successfulRecoveries: 0,
    recoveryRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedNoShow, setSelectedNoShow] = useState<NoShowRecord | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // Fetch no-shows
  useEffect(() => {
    if (!operator) return;
    const controller = new AbortController();

    async function fetchNoShows() {
      setLoading(true);
      setFetchError(null);
      try {
        const params = new URLSearchParams();
        if (selectedBusinessId) {
          params.set('businessId', selectedBusinessId);
        }
        params.set('includeStats', 'true');
        params.set('page', String(currentPage));
        params.set('pageSize', String(pageSize));

        const response = await apiFetch(`/api/operator/no-shows?${params}`, { signal: controller.signal });

        if (controller.signal.aborted) return;
        if (!response.ok) {
          throw new Error('Failed to fetch no-shows');
        }

        const data = await response.json();
        const entries: NoShowRecord[] = data.data || [];
        setNoShows(entries);

        // API returns stats under `data.stats` (not `data.summary`)
        // and only when includeStats=true AND a businessId is selected.
        // Compute summary from entries as a reliable fallback.
        const apiStats = data.stats;
        // API may not return recoveryStatus; treat missing/undefined as PENDING
        const pendingCount = entries.filter((e: NoShowRecord) => !e.recoveryStatus || e.recoveryStatus === 'PENDING').length;
        const rescheduledCount = entries.filter((e: NoShowRecord) => e.recoveryStatus === 'RESCHEDULED').length;
        setSummary({
          totalNoShows: apiStats?.totalNoShows ?? entries.length,
          pendingRecovery: pendingCount,
          successfulRecoveries: rescheduledCount,
          recoveryRate: apiStats?.recoveryRate != null
            ? apiStats.recoveryRate / 100
            : entries.length > 0
              ? rescheduledCount / entries.length
              : 0,
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        console.error('Failed to fetch no-shows:', error);
        setFetchError(error instanceof Error ? error.message : 'Failed to fetch no-shows');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetchNoShows();
    return () => controller.abort();
  }, [selectedBusinessId, operator, currentPage, pageSize]);

  // Filter no-shows
  const filteredNoShows = useMemo(() => {
    let result = [...noShows];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (ns) =>
          ns.customerName.toLowerCase().includes(query) ||
          ns.customerPhone.includes(query) ||
          ns.serviceName.toLowerCase().includes(query)
      );
    }

    if (statusFilter && statusFilter !== 'ALL') {
      result = result.filter((ns) => ns.recoveryStatus === statusFilter);
    }

    return result;
  }, [noShows, searchQuery, statusFilter]);

  // Handlers
  const handleTriggerRecovery = useCallback(async (bookingId: string) => {
    setActionLoading(bookingId);
    try {
      const response = await apiFetch(`/api/operator/no-shows/${bookingId}/recover`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to trigger recovery');
      }

      // Refresh the list
      const params = new URLSearchParams();
      if (selectedBusinessId) {
        params.set('businessId', selectedBusinessId);
      }
      const refreshResponse = await apiFetch(`/api/operator/no-shows?${params}`);
      const data = await refreshResponse.json();
      setNoShows(data.data || []);
    } catch (error) {
      console.error('Failed to trigger recovery:', error);
      alert('Failed to trigger recovery. Please try again.');
    } finally {
      setActionLoading(null);
    }
  }, [operator, selectedBusinessId]);

  const handleViewDetails = useCallback((noShow: NoShowRecord) => {
    setSelectedNoShow(noShow);
    setIsDetailOpen(true);
  }, []);

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  const recoveryStatuses = ['ALL', 'PENDING', 'MESSAGE_SENT', 'RESPONDED', 'RESCHEDULED', 'CLOSED', 'EXPIRED'];

  return (
    <div className="p-6 space-y-6">
      {/* Error Banner */}
      {fetchError && (
        <div className="rounded-lg border border-destructive p-4 mb-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <UserX className="h-6 w-6 text-destructive" />
            {t('noShows.title')}
          </h1>
          <p className="text-muted-foreground">
            {selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')} - {t('noShows.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('noShows.totalNoShows')}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalNoShows}</div>
            <p className="text-xs text-muted-foreground">{t('noShows.allTime')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('noShows.pendingRecovery')}</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.pendingRecovery}</div>
            <p className="text-xs text-muted-foreground">{t('noShows.awaitingAction')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('noShows.successfulRecoveries')}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.successfulRecoveries}</div>
            <p className="text-xs text-muted-foreground">{t('noShows.rescheduled')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('noShows.recoveryRate')}</CardTitle>
            <TrendingDown className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(summary.recoveryRate * 100).toFixed(1)}%
            </div>
            <Progress value={summary.recoveryRate * 100} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('noShows.searchPlaceholderLong')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('noShows.recoveryStatus')} />
              </SelectTrigger>
              <SelectContent>
                {recoveryStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status === 'ALL' ? t('noShows.allStatuses') : status.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* No-Shows Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('noShows.customer')}</TableHead>
                <TableHead>{t('noShows.service')}</TableHead>
                <TableHead>{t('noShows.scheduledFor')}</TableHead>
                <TableHead>{t('noShows.reliability')}</TableHead>
                <TableHead>{t('noShows.recoveryStatus')}</TableHead>
                <TableHead>{t('noShows.attempts')}</TableHead>
                <TableHead className="text-right">{t('noShows.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNoShows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    {t('noShows.noNoShowsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredNoShows.map((noShow) => {
                  const reliability = getReliabilityBadge(noShow.reliabilityScore);
                  const status = getRecoveryStatusBadge(noShow.recoveryStatus);

                  return (
                    <TableRow key={noShow.id}>
                      <TableCell>
                        <div>
                          <button
                            className="font-medium text-left hover:text-primary hover:underline transition-colors"
                            onClick={() => navigate({ to: `/operator/customers/${noShow.customerId}` })}
                          >
                            {noShow.customerName}
                          </button>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {noShow.customerPhone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{noShow.serviceName}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatCurrency(noShow.servicePrice)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{formatDate(noShow.scheduledAt)}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatTime(noShow.scheduledAt)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${getReliabilityColor(noShow.reliabilityScore)}`}>
                                  {noShow.reliabilityScore}
                                </span>
                                <Badge variant={reliability.variant} className="text-xs">
                                  {t('noShows.' + reliability.label)}
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{noShow.totalNoShows} no-shows out of {noShow.totalBookings} bookings</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant} className="flex items-center gap-1 w-fit">
                          {status.icon}
                          {t('noShows.' + status.label)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {noShow.recoveryAttempts}/3
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {noShow.recoveryStatus === 'PENDING' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary hover:text-primary/80"
                              title={t('noShows.sendRecoveryMessage')}
                              onClick={() => handleTriggerRecovery(noShow.bookingId)}
                              disabled={actionLoading === noShow.bookingId}
                            >
                              {actionLoading === noShow.bookingId ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleViewDetails(noShow)}
                            title={t('noShows.viewDetails')}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedNoShow && (
            <>
              <SheetHeader>
                <SheetTitle>{t('noShows.noShowDetails')}</SheetTitle>
                <SheetDescription>
                  Booking ID: {selectedNoShow.bookingId.slice(-8)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Customer Info */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">{t('noShows.customer')}</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="font-medium">{selectedNoShow.customerName}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {selectedNoShow.customerPhone}
                      </div>
                      <Separator className="my-3" />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{t('noShows.reliabilityScore')}</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${getReliabilityColor(selectedNoShow.reliabilityScore)}`}>
                            {selectedNoShow.reliabilityScore}
                          </span>
                          <Badge variant={getReliabilityBadge(selectedNoShow.reliabilityScore).variant}>
                            {t('noShows.' + getReliabilityBadge(selectedNoShow.reliabilityScore).label)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-muted-foreground">{t('noShows.noShowHistory')}</span>
                        <span className="text-sm">
                          {selectedNoShow.totalNoShows} / {selectedNoShow.totalBookings} bookings
                        </span>
                      </div>
                      {selectedNoShow.totalNoShows >= 3 && (
                        <div className="mt-3 p-2 bg-destructive/10 rounded-md flex items-center gap-2">
                          <Shield className="h-4 w-4 text-destructive" />
                          <span className="text-sm text-destructive">
                            {t('noShows.customerMayBeBlocked')}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Appointment Info */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">{t('noShows.missedAppointment')}</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="font-medium">{selectedNoShow.serviceName}</div>
                      <div className="text-sm text-muted-foreground">
                        {selectedNoShow.staffName || t('noShows.noStaffAssigned')}
                      </div>
                      <Separator className="my-3" />
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDate(selectedNoShow.scheduledAt)} at {formatTime(selectedNoShow.scheduledAt)}
                      </div>
                      <div className="mt-2 text-lg font-semibold">
                        {formatCurrency(selectedNoShow.servicePrice)}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recovery Status */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">{t('noShows.recoveryStatus')}</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <span>{t('common.status')}</span>
                        <Badge variant={getRecoveryStatusBadge(selectedNoShow.recoveryStatus).variant}>
                          {t('noShows.' + getRecoveryStatusBadge(selectedNoShow.recoveryStatus).label)}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span>{t('noShows.recoveryAttempts')}</span>
                        <span>{selectedNoShow.recoveryAttempts} / 3</span>
                      </div>
                      <Progress
                        value={(selectedNoShow.recoveryAttempts / 3) * 100}
                        className="h-2 mt-2"
                      />
                    </CardContent>
                  </Card>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex gap-2">
                  {selectedNoShow.recoveryStatus === 'PENDING' && (
                    <Button
                      className="flex-1"
                      onClick={() => {
                        handleTriggerRecovery(selectedNoShow.bookingId);
                        setIsDetailOpen(false);
                      }}
                      disabled={actionLoading === selectedNoShow.bookingId}
                    >
                      {actionLoading === selectedNoShow.bookingId ? (
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      {t('noShows.sendRecoveryMessage')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: `/operator/bookings/${selectedNoShow.bookingId}` })}
                  >
                    {t('noShows.viewBooking')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/no-shows')({
  component: NoShowsPage,
});
